import { type ComponentProps, type FormEvent, useState } from 'react';

import { AuthAside } from '@/components/auth-aside';
import { GoogleSignInButton } from '@/components/google-sign-in-button';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { supabase } from '../lib/supabase.ts';

const MIN_PASSWORD_LENGTH = 8;

interface SignupFormProps extends ComponentProps<'div'> {
  /** Hands the screen back to the sign-in form. */
  onSwitchToLogin: () => void;
}

/**
 * shadcn's signup-04 block, wired to Supabase password auth.
 *
 * Two things the block does not know about this project:
 *
 * - Google is the only external provider `supabase/config.toml` enables, so
 *   the Apple/Google/Meta row is cut down to it rather than rendered dead.
 *   Google accounts arrive already confirmed and skip the panel below.
 * - `auth.email.enable_confirmations` is on, so a successful sign-up returns no
 *   session. Confirmation is a real state of this flow, not an edge case, and
 *   gets its own panel instead of a silent no-op.
 *
 * A new account carries no staff claims until it is granted membership, so
 * signing up gets you a session and an empty console — the grant is a separate,
 * server-side act.
 */
export function SignupForm({ className, onSwitchToLogin, ...props }: SignupFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmationSentTo, setConfirmationSentTo] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    // Caught here only to spare a round trip; Supabase enforces both.
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });

    setIsSubmitting(false);

    if (signUpError) {
      setError(signUpError.message);
      return;
    }

    // With confirmations on there is no session yet — say so, rather than
    // leaving the form looking like it did nothing.
    if (!data.session) {
      setConfirmationSentTo(email);
    }
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          {confirmationSentTo ? (
            <div className="flex flex-col justify-center gap-4 p-6 text-center md:p-8">
              <h1 className="text-2xl font-bold">Check your email</h1>
              <p className="text-muted-foreground text-balance">
                We sent a confirmation link to{' '}
                <span className="font-medium">{confirmationSentTo}</span>. Open it to finish
                creating your account.
              </p>
              <Button variant="outline" onClick={onSwitchToLogin}>
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 md:p-8">
              <FieldGroup>
                <div className="flex flex-col items-center gap-2 text-center">
                  <h1 className="text-2xl font-bold">Create your account</h1>
                  <p className="text-muted-foreground text-sm text-balance">
                    Enter your email below to create your account
                  </p>
                </div>

                <Field>
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <FieldDescription>
                    We&apos;ll send a confirmation link here before the account is active.
                  </FieldDescription>
                </Field>

                <Field>
                  <Field className="grid grid-cols-2 gap-4">
                    <Field>
                      <FieldLabel htmlFor="password">Password</FieldLabel>
                      <Input
                        id="password"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                    </Field>

                    <Field>
                      <FieldLabel htmlFor="confirm-password">Confirm password</FieldLabel>
                      <Input
                        id="confirm-password"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                      />
                    </Field>
                  </Field>
                  <FieldDescription>
                    Must be at least {MIN_PASSWORD_LENGTH} characters long.
                  </FieldDescription>
                </Field>

                {error && <FieldError>{error}</FieldError>}

                <Field>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating account…' : 'Create account'}
                  </Button>
                </Field>

                <GoogleSignInButton label="Sign up" onError={setError} />

                <FieldDescription className="text-center">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    className="underline underline-offset-4"
                  >
                    Sign in
                  </button>
                </FieldDescription>
              </FieldGroup>
            </form>
          )}

          <AuthAside />
        </CardContent>
      </Card>
    </div>
  );
}
